import { useCallback, useEffect, useState } from "react";
import APIutil from "api/APIutils";
import * as url from "api/url";
import { useSelector, useDispatch } from "react-redux";
import { getAllResources } from 'action/cultureAction';
import { signCertificateErrors } from "./constants";

export const useCertificateDeleteAPIMethods = (setState, getCertificateData) => {
    const deleteCertificate = useCallback((certificateId) => {
        setState(prevState => ({ ...prevState, loading: true, apiError: "" }));
        APIutil.deleteAPI(`${url.delete_operational_certificate}/${certificateId}`).then(response => {
            if (response && response.status === 200) {
                setState(prevState => ({
                    ...prevState,
                    isModelOpen: false,
                    deleteItem: null
                }));
                getCertificateData();
            } else {
                const message = response && response.statusText ? response.statusText : "Something went wrong. Please try again."
                setState(prevState => ({ ...prevState, loading: false, apiError: message }));
            }
        }).catch(error => {
            setState(prevState => ({ ...prevState, loading: false, apiError: "Something went wrong. Please try again." }));
            throw error;
        })
    }, [setState]);
    
    return {
        deleteCertificate
    };
}

export const useFetchHubParamsData = (history) => {
    const [state, setState] = useState({
        loading: false,
        error: false,
        errorMessage: "",
        certificateDetails: null,
        data: {}
    });

    useEffect(() => {
        fetchData();
    },[])

    const fetchData = useCallback((payload, suppressError) => {
        if (payload && (payload.editMode)) {
            return null;
        } else {
            setState(prevState => ({ ...prevState, loading: true }));
            APIutil.getDataAPI(url.get_hub_parameter_details).then(response => {
                if (response && !response.statusText) {
                        setState(prevState => ({ ...prevState, loading: false, data: response, error: false, errorMessage: "" }));
                }else {
                    const message = response && response.statusText ? response.statusText : "Something went wrong. Please try refreshing the page."
                    if (suppressError) {
                        setState(prevState => ({ ...prevState, loading: false }));
                    } else {
                        setState(prevState => ({ ...prevState, loading: false, error:true, errorMessage: message }));
                    }
                }
            }).catch(error => {
                if (suppressError) {
                    setState(prevState => ({ ...prevState, loading: false }));
                } else {
                    setState(prevState => ({ ...prevState, loading: false, error: true, errorMessage: "Something went wrong. Please try refreshing the page." }));
                }
                throw error;
            });
        }
    }, [setState]);

    const getCertificateDetail = useCallback((payload, suppressError) => {
            setState(prevState => ({ ...prevState, loading: true }));
            return APIutil.getDataAPI(url.get_root_cert_details).then(response => {
                    setState(prevState => ({ ...prevState, loading: false, certificateDetails: (response && !response.statusText) ? response : null
                }));
            }).catch(error => {
                if (suppressError) {
                    setState(prevState => ({ ...prevState, loading: false }));
                } else {
                    setState(prevState => ({ ...prevState, loading: false, error: true, errorMessage: "Unable to get certificate details. Please try refreshing the page." }));
                }
                throw error;
            });
    }, [setState]);

    const downloadRootCertificate = useCallback((fileName) => {
        setState(prevState => ({ ...prevState, error: false, errorMessage: "" }));
        return APIutil.getPDFDataAPI(url.download_root_certificate)
            .then(response => {
                if (response && !response.statusText) {
                    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                        window.navigator.msSaveOrOpenBlob(new Blob([response]), fileName);
                    } else {
                        const url = window.URL.createObjectURL(new Blob([response]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileName);
                        document.body.appendChild(link);
                        link.click();
                    }
                } else {
                    const messageContent = "Unable to download root certificate details. Please try again."
                    setState(prevState => ({ ...prevState, error: true, errorMessage: messageContent }));
                }
            })
            .catch(error => {
                const messageContent = "Unable to download root certificate details. Please try again."
                setState(prevState => ({ ...prevState, error: true, errorMessage: messageContent }));
                throw error;
            })
    }, [setState]);

    return {
        loading: state.loading,
        error: state.error,
        errorMessage: state.errorMessage,
        setAPIHookData: setState,
        certificateDetails: state.certificateDetails,
        getCertificateDetail,
        downloadRootCertificate,
        data: state.data,
        refetch: fetchData,
    };
}

export const useGetLocalizationLabel = () => {
    const systemAdminLabels = useSelector(state => state.CulturalLabelsRootReducer.SystemAdmin_LabelsBasedOnPreference);
    const dispatch = useDispatch();
    useEffect(() => {
        if (systemAdminLabels["ManageCertificateSigning.HeaderTitle.Text"] === undefined) {
            dispatch(getAllResources());
        }
    }, []);

    const getLabel = (labelKey) => {
        if (systemAdminLabels && systemAdminLabels[`ManageCertificateSigning.${labelKey}.Text`]) {
            return systemAdminLabels[`ManageCertificateSigning.${labelKey}.Text`];
        } else {
            return "";
        }
    }

    const getErrorLabel = (labelKey) => {
        if (systemAdminLabels && systemAdminLabels[`ManageCertificateSigning.Error.${labelKey}.Text`]) {
            return systemAdminLabels[`ManageCertificateSigning.Error.${labelKey}.Text`];
        } else {
            return signCertificateErrors[labelKey] ? signCertificateErrors[labelKey] : "Unknown error has occurred, please try again.";
        }
    }

    return {
        systemAdminLabels,
        getLabel,
        getErrorLabel
    }
}
